import React from 'react';
import ProjectsReal from './components/ProjectsReal';
import CommandPalette from './components/CommandPalette';
import StatTiles from './components/StatTiles';
import CommitChart from './components/CommitChart';
import useEasterEggs from './hooks/useEasterEggs';
import PROJECTS from './config/projects';

const EMAIL = 'shailysharmawork@gmail.com';
const GITHUB = 'https://github.com/SHAILY24';
const LINKEDIN = 'https://linkedin.com/in/shailysharma873';
const UPWORK = 'https://www.upwork.com/freelancers/shailydev';

const LIVE = PROJECTS.filter((p) => p.live);

const EXPERIENCE = [
  {
    when: 'May 2025 to now',
    role: 'Data Engineer / Senior Developer',
    org: 'PureHD',
    desc:
      'B2B e-commerce and integrations platform on React/Vite, FastAPI and PostgreSQL/Redis. I built the multi-vendor API gateway and the order automation behind it.',
    tags: ['React/Vite', 'FastAPI', 'PostgreSQL', 'Redis'],
  },
  {
    when: 'Jan to Jun 2025',
    role: 'Data Engineering Consultant',
    org: 'Fairway Vision',
    desc:
      'Computer vision and pipeline work for an AR golf product. Ball tracking, trajectory modeling, and the mobile data layer underneath it.',
    tags: ['Computer vision', 'Pipelines', 'Mobile data'],
  },
  {
    when: 'Jun 2024 to Jan 2025',
    role: 'Technical BA / Data Engineer',
    org: 'Jersey City Community Court',
    desc:
      'An offline-first encrypted field-assessment system for caseworkers, plus the legacy data migration and the case-management dashboard that sat on top of it.',
    tags: ['Offline-first', 'Data migration', 'Dashboards'],
  },
  {
    when: 'Jul 2018 to Dec 2023',
    role: 'Senior Test Lead / Automation Engineer / BA',
    org: 'Wipro / Credit Suisse (India & Switzerland)',
    desc:
      'Payments and risk platforms. I built test and API automation frameworks and led the team across India and Switzerland.',
    tags: ['Test automation', 'API automation', 'Team lead'],
  },
];

const EDUCATION = [
  {
    deg: 'MS, Data Science',
    inst: 'New Jersey Institute of Technology (NJIT)',
    yr: 'Jan 2024 to Aug 2025',
  },
  {
    deg: 'MBA, Business Analytics & Finance',
    inst: 'Symbiosis International University, Pune',
    yr: '2020 to 2022',
  },
  {
    deg: 'B.Tech, Information Technology',
    inst: 'Maharaja Agrasen Institute of Technology (MAIT), Delhi',
    yr: '2015 to 2018',
  },
];

const CERTS = [
  {
    nm: 'AWS Certified Data Engineer, Associate',
    iss: 'Amazon Web Services',
    url:
      'https://www.credly.com/badges/2c7079a2-f843-43aa-b3c1-08b41636d90a/linked_in_profile',
  },
  {
    nm: 'AWS Certified Cloud Practitioner',
    iss: 'Amazon Web Services · Jan 2026 to Jan 2029',
    url:
      'https://www.credly.com/badges/43c3820e-2835-48a7-b7b4-61e8fa09ba79/linked_in_profile',
  },
  { nm: 'Microsoft Azure Fundamentals (AZ-900)', iss: 'Microsoft' },
  { nm: 'ITIL v3 Foundation', iss: 'AXELOS' },
];

const SKILLS = [
  'Python', 'FastAPI', 'Flask', 'React 18', 'TypeScript', 'PostgreSQL',
  'Redis', 'Docker', 'Playwright', 'Selenium', 'BeautifulSoup', 'Stripe',
  'scikit-learn', 'pandas', 'D3.js', 'Nginx',
];

function App() {
  const { cli, banner } = useEasterEggs();
  const [paletteOpen, setPaletteOpen] = React.useState(false);

  React.useEffect(() => {
    // %c console note for whoever opens devtools (recruiter / dev reading source)
    const head =
      "color:oklch(46% 0.13 258);font:600 14px/1.5 'Fira Code',monospace";
    const body = "color:#555;font:13px/1.6 'Fira Code',monospace";
    /* eslint-disable no-console */
    console.log('%cShaily Sharma / Python + React / data engineer', head);
    console.log(
      '%cYou opened devtools, so you read code. Every number here is a\n' +
        'link. 13 of these demos are deployed and running. The 17 repos\n' +
        'are public. The commit chart is counted from those repos, gaps\n' +
        'and all. Press / for the palette. The Konami code flips a\n' +
        'terminal theme if you want to see it.\n' +
        'Mail is ' + EMAIL + '.',
      body
    );
    /* eslint-enable no-console */
  }, []);

  return (
    <>
      <a className="skip-link" href="#work">Skip to projects</a>

      {cli && (
        <div className="cli-banner" role="status">
          <div className="wrap">
            cli theme on. Run "toggle theme" in the palette to go back, or
            press the code again.
          </div>
        </div>
      )}

      <header className="site">
        <div className="aurora" aria-hidden="true">
          <span className="aurora-band band-1" />
          <span className="aurora-band band-2" />
          <span className="aurora-band band-3" />
        </div>
        <div className="wrap">
          <p className="eyebrow">Shaily Sharma</p>
          <h1>Python + React Developer / Data Engineer</h1>
          <p className="title">
            I write scrapers, automation, API integrations and payment flows,
            and the data pipelines that feed them.
          </p>
          <p className="locline">
            Jersey City, NJ · open to remote ·{' '}
            <a href={UPWORK} rel="noopener">Top Rated on Upwork</a>
          </p>
          <p className="evidence">
            Most of this is public. 17 repositories on GitHub. 13 of them are
            deployed and you can open them right now. I would rather you click
            than read a claim.
          </p>
          <div className="links">
            <a href={GITHUB} data-primary>github</a>
            <a href={UPWORK} rel="noopener">upwork</a>
            <a href={LINKEDIN} rel="noopener">linkedin</a>
            <a href={`mailto:${EMAIL}`}>email</a>
            <a href="#work">projects</a>
          </div>

          <StatTiles />

          <CommitChart />

          <div className="live-strip">
            <p className="lab">{'// 13 deployments, open any of them'}</p>
            <div className="live-grid">
              {LIVE.map((p) => (
                <a
                  key={p.name}
                  className="live-cell"
                  href={p.live}
                  rel="noopener"
                >
                  <span className="nm">{p.name}</span>
                  <span className="ur">
                    {p.live.replace('https://', '')}
                    {p.liveNote && (
                      <span className="ln"> · {p.liveNote}</span>
                    )}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="aurora-rule" aria-hidden="true" />

      <main>
        <section id="about">
          <div className="wrap">
            <p className="sec-label">{'// about'}</p>
            <h2>How I work</h2>
            <p className="prose">
              I keep scope small and I say what I mean. I would rather hand you
              something that runs than something that demos well in a call.
              That is why most of what I build ends up public. The work I am
              best at is the unglamorous part: the integration code where a
              pipeline has to talk to an API that has to feed a UI, and none of
              the three quite agree on the shape of the data. I have spent the
              last few years in that seam, between data engineering and
              full-stack.
            </p>
            <div className="skills">
              {SKILLS.map((s) => (
                <span key={s}>{s}</span>
              ))}
            </div>
          </div>
        </section>

        <div className="aurora-rule thin" aria-hidden="true" />

        <section id="work">
          <div className="wrap">
            <p className="sec-label">{'// work · 17 public repositories'}</p>
            <h2>Projects</h2>
            <p className="prose">
              Sort any column. Filter by band. The live ones float to the top
              by default and each link goes to the deployment, not a video of
              it. Open a row and I tell you what the problem was, how I built
              it, what I gave up to do it that way, and how auth is handled.
            </p>
            <ProjectsReal />
          </div>
        </section>

        <div className="aurora-rule thin" aria-hidden="true" />

        <section id="ai">
          <div className="wrap">
            <p className="sec-label">{'// how I work with AI'}</p>
            <h2>How I actually use AI tools</h2>
            <p className="prose">
              I use Cursor, Claude Code and Codex. I am not going to pretend I
              do not. They are good at the mechanical stuff: a FastAPI route
              skeleton, a first draft of a test, a regex I would otherwise go
              look up. That genuinely saves me time.
            </p>
            <p className="prose" style={{ marginTop: '0.8rem' }}>
              They do not get to make decisions. The architecture, the data
              model, the trade-offs, those are mine, and I read every line
              before it goes into a commit. No vibe-coding. If I cannot walk
              you through why a piece of code is the way it is, it does not
              ship. When it breaks at 2am it is my name on the blame.
            </p>
          </div>
        </section>

        <div className="aurora-rule thin" aria-hidden="true" />

        <section id="experience">
          <div className="wrap">
            <p className="sec-label">{'// experience'}</p>
            <h2>Background</h2>
            <div className="xp">
              {EXPERIENCE.map((x) => (
                <div className="xp-row" key={x.org}>
                  <span className="xp-when num">{x.when}</span>
                  <div>
                    <div>
                      <span className="xp-role">{x.role}</span> ·{' '}
                      <span className="xp-org">{x.org}</span>
                    </div>
                    <p className="xp-desc">{x.desc}</p>
                    <div className="xp-tags">
                      {x.tags.map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="education">
          <div className="wrap">
            <p className="sec-label">{'// education'}</p>
            <h2>Degrees</h2>
            <div>
              {EDUCATION.map((e) => (
                <div className="edu-row" key={e.deg}>
                  <div>
                    <div className="edu-deg">{e.deg}</div>
                    <div className="edu-inst">{e.inst}</div>
                  </div>
                  <span className="edu-yr num">{e.yr}</span>
                </div>
              ))}
            </div>
            <p className="edu-note">
              Teaching assistant for CS 610 (Data Structures &amp; Algorithms)
              at NJIT.
            </p>
          </div>
        </section>

        <section id="uses">
          <div className="wrap">
            <p className="sec-label">{'// tools'}</p>
            <h2>What I work with</h2>
            <p className="prose">
              The actual tools, not a sponsored list. Nothing here has an
              affiliate link behind it.
            </p>
            <details className="uses" id="uses-disclosure">
              <summary>
                <span>/uses</span>
                <span className="hint">click to expand</span>
              </summary>
              <div className="body">
                <dl>
                  <dt>editor</dt>
                  <dd>
                    nano for a one-line fix. Cursor for a real session. Either
                    way the shell is where most of it happens.
                  </dd>
                  <dt>languages</dt>
                  <dd>Python first. TypeScript when it ships to a browser. SQL every day.</dd>
                  <dt>backend</dt>
                  <dd>FastAPI, sometimes Flask. PostgreSQL with materialized views where the reads hurt. Redis in front of it.</dd>
                  <dt>frontend</dt>
                  <dd>React 18 on Vite. Plain CSS. I pull in D3 only when a chart actually earns it.</dd>
                  <dt>automation</dt>
                  <dd>Playwright and Selenium for the browser, BeautifulSoup for parsing what comes back.</dd>
                  <dt>ml</dt>
                  <dd>scikit-learn and pandas. SHAP when a score needs explaining. PyCaret if AutoML is the right call.</dd>
                  <dt>shell</dt>
                  <dd>
                    zsh and tmux all day. ripgrep and fzf to move around a
                    codebase. jq for anything that hands back JSON.
                  </dd>
                  <dt>infra</dt>
                  <dd>Docker Compose, Nginx, GitHub Actions. It runs on a self-hosted box behind a port finder I wrote.</dd>
                </dl>
              </div>
            </details>
          </div>
        </section>

        <section id="certs">
          <div className="wrap">
            <p className="sec-label">{'// certifications'}</p>
            <h2>Verified credentials</h2>
            <div className="certs">
              {CERTS.map((c) => (
                <div className="cert" key={c.nm}>
                  <span className="nm">{c.nm}</span>
                  <span className="iss">{c.iss}</span>
                  {c.url && (
                    <a href={c.url} rel="noopener">verify on Credly →</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="wrap">
            <p className="sec-label">{'// contact'}</p>
            <h2>Get in touch</h2>
            <p className="prose">
              Tell me what you need built, scraped or wired into something
              else, and I'll come back with how I'd actually do it and what
              it'd take. Based in Jersey City, NJ, work remote. Top Rated on
              Upwork.
            </p>
            <div className="contact-block">
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              <a href={UPWORK} rel="noopener">upwork.com/freelancers/shailydev</a>
              <a href={GITHUB} rel="noopener">github.com/SHAILY24</a>
              <a href={LINKEDIN} rel="noopener">linkedin.com/in/shailysharma873</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site">
        <div className="wrap">
          <p>
            Every row links to its repo. Every "live" row links to the
            deployment behind it. Go check.
          </p>
          <p className="hint-line">
            Press <kbd>/</kbd> or <kbd>Cmd</kbd>+<kbd>K</kbd> for the command
            palette. There's a /uses list above, and a terminal theme if you
            remember the old code.
          </p>
        </div>
      </footer>

      <CommandPalette
        open={paletteOpen}
        setOpen={setPaletteOpen}
        email={EMAIL}
        github={GITHUB}
      />

      {banner}
    </>
  );
}

export default App;
